import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../providers/AuthProvider';
import { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useSupportPresence } from './useSupportPresence';

export interface SupportMessage {
  id: string;
  ticket_id: string;
  user_id: string;
  message: string;
  is_from_admin: boolean;
  read_at: string | null;
  created_at: string;
}

export interface SupportTicket {
  id: string;
  user_id: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  user?: {
    email: string;
    user_metadata?: {
      name?: string;
    };
  };
}

export function useSupportChat(ticketId: string | null) {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  
  // Presença e typing
  const { onlineUsers, typingUsers, sendTyping } = useSupportPresence(ticketId, true);

  // Carregar mensagens iniciais
  const loadMessages = useCallback(async () => {
    if (!ticketId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      // Carregar ticket com informações do usuário
      const { data: ticketData, error: ticketError } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (ticketError) throw ticketError;

      // Buscar informações do usuário
      const { data: userData } = await supabase.auth.admin.getUserById(ticketData.user_id);
      
      setTicket({
        ...ticketData,
        user: {
          email: userData?.user?.email || '',
          user_metadata: userData?.user?.user_metadata,
        },
      } as SupportTicket);

      // Carregar mensagens
      const { data: messagesData, error: messagesError } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;
      setMessages((messagesData || []) as SupportMessage[]);
    } catch (error: unknown) {
      console.error('Error loading messages:', error);
      const errMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error('Erro ao carregar mensagens: ' + errMessage);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Enviar mensagem como admin
  const sendMessage = useCallback(async (message: string) => {
    if (!ticketId || !message.trim()) return;

    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          ticket_id: ticketId,
          user_id: user.id,
          message: message.trim(),
          is_from_admin: true,
        })
        .select()
        .single();

      if (error) throw error;

      // Atualizar status do ticket para 'in_progress' se estava 'open'
      await supabase
        .from('support_tickets')
        .update({ 
          status: 'in_progress',
          updated_at: new Date().toISOString(),
        })
        .eq('id', ticketId)
        .eq('status', 'open');

      return data as SupportMessage;
    } catch (error: unknown) {
      console.error('Error sending message:', error);
      const errMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error('Erro ao enviar mensagem: ' + errMessage);
      throw error;
    } finally {
      setSending(false);
    }
  }, [ticketId]);

  // Atualizar status do ticket
  const updateTicketStatus = useCallback(async (status: SupportTicket['status']) => {
    if (!ticketId) return;

    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', ticketId);


      if (error) throw error;
      toast.success('Status atualizado!');
    } catch (error: unknown) {
      console.error('Error updating ticket:', error);
      const errMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error('Erro ao atualizar status: ' + errMessage);
    }
  }, [ticketId]);

  // Configurar Realtime subscription
  useEffect(() => {
    if (!ticketId) {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      return;
    }

    // Carregar mensagens iniciais
    loadMessages();

    // Configurar canal Realtime
    const channel = supabase
      .channel(`support_messages:${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => {
          const newMessage = payload.new as SupportMessage;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMessage.id)) {
              return prev;
            }
            return [...prev, newMessage];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as SupportMessage;
          setMessages((prev) =>
            prev.map((m) => (m.id === updatedMessage.id ? updatedMessage : m))
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_tickets',
          filter: `id=eq.${ticketId}`,
        },
        (payload) => {
          setTicket((prev) => ({
            ...(prev || {}),
            ...payload.new,
          } as SupportTicket));
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Admin subscribed to support messages');
        }
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [ticketId, loadMessages]);

  return {
    messages,
    ticket,
    loading,
    sending,
    sendMessage,
    updateTicketStatus,
    refresh: loadMessages,
    onlineUsers,
    typingUsers,
    sendTyping,
  };
}
